import React, { useCallback, useMemo, useState } from "react";
import { isAxiosError } from "axios";
import { z } from "zod";
import { Account } from "@common/types/domains/accounting/account";
import { displayAccountLabel } from "@common/helpers/accounting";
import { ExtendedAccount } from "@common/types/reports/types";
import { isAccountTypeEligibleForRole } from "@common/constants/domains/accounting/accountRole";
import { useReassignAccountRoleMutation } from "@api/account.api";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useAccounts } from "@hooks/useAccounts";
import { useOverlays } from "contexts/OverlayContext";
import { canOfferMergeAfterReassign } from "./accountActionEligibility";
import {
    buildReassignRoleToastMessage,
    REASSIGN_ROLE_MODAL_CUTOFF_COPY,
} from "./constants";
import { ReassignRoleModalShell } from "./ReassignRoleModalShell";
import { ReassignRoleSuccessToast } from "./ReassignRoleSuccessToast";
import { ReassignRoleTargetPicker } from "./ReassignRoleTargetPicker";

interface Props {
    show?: boolean;
    onHide?: () => void;
    account: ExtendedAccount;
    onReassigned?: () => void;
    onMutation?: (promise: Promise<unknown>) => void;
}

const reassignErrorBodySchema = z.object({
    message: z.string(),
});

export const ReassignAccountRoleModal: React.FC<Props> = ({
    show,
    onHide,
    account,
    onReassigned,
    onMutation,
}) => {
    const { error: toastError, toast, dismiss } = useToaster();
    const { openOverlay } = useOverlays();
    const [targetAccount, setTargetAccount] = useState<Account>();

    const role = account.role ?? null;

    const { accounts, isLoading } = useAccounts({ entityId: account.entityId });

    const eligibleAccounts = useMemo(() => {
        if (role === null) {
            return [];
        }
        return accounts.filter(
            (candidate) =>
                candidate.id !== account.id &&
                candidate.role == null &&
                candidate.relatedEntityId == null &&
                candidate.financialAccountId == null &&
                isAccountTypeEligibleForRole(role, candidate.type),
        );
    }, [accounts, account.id, role]);

    const reassignMutation = useReassignAccountRoleMutation();

    const targetItems = useMemo(
        () =>
            eligibleAccounts.map((candidate) => ({
                value: candidate.id,
                label: displayAccountLabel(candidate, true),
            })),
        [eligibleAccounts],
    );

    const onSelectTarget = useCallback(
        (selectedId: string | null) => {
            setTargetAccount(
                eligibleAccounts.find(
                    (candidate) => candidate.id === selectedId,
                ),
            );
        },
        [eligibleAccounts],
    );

    const onSubmit = useCallback(async () => {
        if (!targetAccount || role === null) {
            return;
        }
        // Track before await so a drawer close mid-request still sees the write.
        const mutation = reassignMutation.mutateAsync({
            entityId: account.entityId,
            body: {
                role,
                targetAccountId: targetAccount.id,
            },
        });
        onMutation?.(mutation);
        try {
            await mutation;
        } catch (error) {
            const parsed = isAxiosError(error)
                ? reassignErrorBodySchema.safeParse(error.response?.data)
                : undefined;
            toastError(
                parsed?.success
                    ? parsed.data.message
                    : "Failed to reassign role",
            );
            return;
        }
        const leftoverAccount = { ...account, role: null };
        const shouldOfferMerge = canOfferMergeAfterReassign({
            sourceAccount: leftoverAccount,
        });
        const toastId = toast(
            <ReassignRoleSuccessToast
                message={buildReassignRoleToastMessage({
                    sourceName: account.name,
                    targetName: targetAccount.name,
                })}
                onMerge={
                    shouldOfferMerge
                        ? () => {
                              dismiss(toastId);
                              openOverlay("mergeChartOfAccounts", {
                                  account: leftoverAccount,
                              });
                          }
                        : undefined
                }
            />,
            undefined,
            shouldOfferMerge ? { preventAutoClose: true } : undefined,
        );
        onHide?.();
        onReassigned?.();
    }, [
        targetAccount,
        role,
        reassignMutation,
        account,
        toast,
        toastError,
        dismiss,
        openOverlay,
        onHide,
        onReassigned,
        onMutation,
    ]);

    return (
        <ReassignRoleModalShell
            show={show}
            onHide={onHide}
            role={role}
            isSubmitDisabled={!targetAccount}
            isSubmitting={reassignMutation.isPending}
            onSubmit={onSubmit}
        >
            <p className="mb-0">{REASSIGN_ROLE_MODAL_CUTOFF_COPY}</p>
            <ReassignRoleTargetPicker
                isLoading={isLoading}
                items={targetItems}
                value={targetAccount?.id ?? null}
                emptyNoun="business"
                onChange={onSelectTarget}
            />
        </ReassignRoleModalShell>
    );
};
